const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/DashboardPage/Cards/AppointmentCards/AppointmentCards.tsx';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { Link, Typography } from '@mui/material';
import textMap from './translations';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { getPatAppSettings } from 'App/utils/generalUtils';
import { goToAppointments } from 'App/utils/history';
import { useFeatureFlags } from 'App/store/featureFlags';
import { selectUpcomingAppointment, useFutureAppointments, usePastAppointments } from 'App/store/appointment';
import { CardNavigationLink } from '../CardNavigationLink';
import { DashboardCard } from '../DashboardCard';
import { StructuredAppointment } from '@cistec/common_web/defs/kisim';
import moment from 'moment';
import { linkStyle, noAppointmentTextCss, noAppointmentTextStyle } from '../../Components/appointmentCards/styles';
import { AppointmentDetails } from '../../Components/appointmentCards/AppointmentDetails';

export const AppointmentCards = (props: {}): any => {
    const { formatMessage } = useIntl();
    const history = useHistory();

    const settings = getPatAppSettings();
    const configuration = getConfiguration();
    const { website } = configuration.urls;

    const { data: featureFlags } = useFeatureFlags();

    const { data: pastAppointments, isPendingOrLoading: isLoadingPastAppointments } = usePastAppointments();
    const { data: futureAppointments, isPendingOrLoading: isLoadingFutureAppointments } = useFutureAppointments();

    const upcomingAppointment = useSelector(selectUpcomingAppointment);
    const hasPastAppointments = !!pastAppointments;
    const hasFutureAppointments = !!futureAppointments;
    const hasAppointments = hasFutureAppointments || hasPastAppointments;

    const renderAppointmentsCard = () => {
        if (!featureFlags.dashboard.calendar) return null;

        const isDataLoading = isLoadingFutureAppointments || (!hasFutureAppointments && isLoadingPastAppointments);
        const handleClick = () => goToAppointments(history);
        return (
            <DashboardCard
                iconClass="icons8-calendar"
                onClick={handleClick}
                isDataLoading={isDataLoading}
                hasData={hasAppointments}
            >
                <CardNavigationLink text={textMap.cardTitleAppointment} />
            </DashboardCard>
        );
    };

    const renderNoUpcomingAppointment = () => {
        return (
            <>
                <Typography component="h2" sx={{ color: 'primary.main' }} variant="subtitle1">
                    {formatMessage(textMap.nextAppointment)}
                </Typography>
                <Typography
                    variant="subtitle1"
                    component="h2"
                    sx={{
                        color: 'common.black',
                    }}
                >
                    {formatMessage(textMap.noAppointmentWithEmailLoginSubtitle)}
                </Typography>
                {featureFlags.dashboard.appointmentBooking && (
                    <div className={noAppointmentTextStyle}>
                        <Typography className={noAppointmentTextCss}>
                            {formatMessage(textMap.noAppointmentText, {
                                hospitalName: (
                                    <Link className={linkStyle} href={website}>
                                        {settings.hospitalLongName}
                                    </Link>
                                ),
                            })}
                        </Typography>
                    </div>
                )}
            </>
        );
    };

    const renderUpcomingAppointmentCard = () => {
        if (!featureFlags.dashboard.yourNextAppointment) return null;

        const getCardTitle = (appointment: StructuredAppointment) => {
            const now = moment();
            if (now.isBetween(appointment.STARTDAT, appointment.STOPDAT, undefined, '[]')) {
                return textMap.currentAppointment;
            }
            return textMap.nextAppointment;
        };

        return (
            <DashboardCard iconClass="icons8-walking" isDataLoading={isLoadingFutureAppointments}>
                {upcomingAppointment ? (
                    <AppointmentDetails
                        appointment={upcomingAppointment}
                        title={getCardTitle(upcomingAppointment)}
                        hideAppointmentConfirmButton={featureFlags.dashboard.showSurgeryCard || true}
                    />
                ) : (
                    renderNoUpcomingAppointment()
                )}
            </DashboardCard>
        );
    };

    return (
        <>
            {renderUpcomingAppointmentCard()}
            {renderAppointmentsCard()}
        </>
    );
};
